import { getAssessmentScoreCategory } from "@repo/app-types/constants/assessmentScore";
import { Color } from "@repo/app-types/constants/colors";
import { getPassRateColor } from "@repo/app-types/constants/passRate";
import type { PlaybookCriterionNode } from "@repo/app-types/entities/playbook";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { buildScoreBucketRanges } from "@repo/app-types/helpers/gradingAssessmentHelpers";
import { AnchoredTooltip } from "@repo/ui/tooltip/anchoredTooltip";
import { type FC, useLayoutEffect, useMemo, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";

export interface ScoreRange {
  min: number;
  max: number;
  label: string;
  teammateIds: TeammateId[];
}

interface CriteriaScoreDistributionChartProps {
  teammates: readonly { teammateId: TeammateId; score: number }[];
  gradingValueType: PlaybookCriterionNode["grading_value_type"];
  selectedRange: ScoreRange | null;
  onRangeSelect: (range: ScoreRange | null) => void;
}

interface BarColors {
  lightFill: string;
  stroke: string;
}

interface DistributionBucket {
  range: ScoreRange;
  count: number;
  colors: BarColors;
}

const grayColorClasses: BarColors = {
  lightFill: "fill-gray-300",
  stroke: "stroke-gray-400",
};

// Consolidated color mappings
const colorToClasses: Record<string, BarColors> = {
  [Color.Emerald]: {
    lightFill: "fill-emerald-300",
    stroke: "stroke-emerald-400",
  },
  [Color.Green]: {
    lightFill: "fill-green-300",
    stroke: "stroke-green-400",
  },
  [Color.Lime]: {
    lightFill: "fill-lime-300",
    stroke: "stroke-lime-400",
  },
  [Color.Amber]: {
    lightFill: "fill-amber-300",
    stroke: "stroke-amber-400",
  },
  [Color.Orange]: {
    lightFill: "fill-orange-300",
    stroke: "stroke-orange-400",
  },
  [Color.Gray]: grayColorClasses,
  [Color.Yellow]: {
    lightFill: "fill-yellow-300",
    stroke: "stroke-yellow-400",
  },
  [Color.Red]: {
    lightFill: "fill-red-300",
    stroke: "stroke-red-400",
  },
};

function getColorClasses(color: string): BarColors {
  return colorToClasses[color] ?? grayColorClasses;
}

function createScoreRanges(
  gradingValueType: PlaybookCriterionNode["grading_value_type"],
): ScoreRange[] {
  return buildScoreBucketRanges(gradingValueType).map((r) => ({
    ...r,
    label:
      gradingValueType === "yes_no"
        ? `${Math.round(r.min * 100)}%`
        : r.min.toFixed(1),
    teammateIds: [],
  }));
}

export function isScoreInRange(
  score: number,
  range: Pick<ScoreRange, "min" | "max">,
  inclusiveMax = false,
): boolean {
  if (inclusiveMax) {
    return score >= range.min && score <= range.max;
  }
  return score >= range.min && score < range.max;
}

function getBarColorForYesNoRange(range: ScoreRange): BarColors {
  // For yes/no, use the pass rate color logic
  const midPoint = (range.min + range.max) / 2;
  const passRateColor = getPassRateColor(midPoint);
  return getColorClasses(passRateColor);
}

function getBarColorFor1To5Range(range: ScoreRange): BarColors {
  // For 1-5 scores, use the middle of the range to determine color
  const midPoint = (range.min + range.max) / 2;
  const roundedScore = Math.round(midPoint);
  const category = getAssessmentScoreCategory(roundedScore);
  return getColorClasses(category.color);
}

const horizontalPadding = 0;
const topPadding = 15;
const bottomPadding = 15;
const leftPadding = 40;

export const CriteriaScoreDistributionChart: FC<
  CriteriaScoreDistributionChartProps
> = ({ teammates, gradingValueType, selectedRange, onRangeSelect }) => {
  const [width, setWidth] = useState(800);
  const [height, setHeight] = useState(200);
  // SVG shapes can't be React Aria tooltip triggers, so the chart tracks the
  // hovered bar itself and anchors one tooltip to it.
  const hoveredBarRef = useRef<SVGRectElement | null>(null);
  const [hoveredTooltipContent, setHoveredTooltipContent] = useState<
    string | null
  >(null);

  const distribution = useMemo(() => {
    const ranges = createScoreRanges(gradingValueType);

    const buckets: DistributionBucket[] = ranges.map((range, idx) => {
      const isLast = idx === ranges.length - 1;
      const teammatesInRange = teammates.filter((t) =>
        isScoreInRange(t.score, range, isLast),
      );

      const rangeWithIds: ScoreRange = {
        ...range,
        teammateIds: teammatesInRange.map((t) => t.teammateId),
      };

      const colors =
        gradingValueType === "yes_no"
          ? getBarColorForYesNoRange(rangeWithIds)
          : getBarColorFor1To5Range(rangeWithIds);

      return {
        range: rangeWithIds,
        count: teammatesInRange.length,
        colors,
      };
    });

    return buckets;
  }, [teammates, gradingValueType]);

  const maxCount = Math.max(...distribution.map((b) => b.count), 1);

  const isRangeSelected = (range: ScoreRange): boolean => {
    if (!selectedRange) {
      return false;
    }

    return selectedRange.min === range.min && selectedRange.max === range.max;
  };

  const getTooltipContent = (bucket: DistributionBucket): string => {
    if (bucket.count === 0) return "";

    const rangeDisplay =
      gradingValueType === "yes_no"
        ? `${(bucket.range.min * 100).toFixed(0)}-${(bucket.range.max * 100).toFixed(0)}%`
        : `${bucket.range.min.toFixed(1)}-${bucket.range.max.toFixed(1)}`;

    const teammateLabel = bucket.count === 1 ? "teammate" : "teammates";
    return `${rangeDisplay}: ${bucket.count} ${teammateLabel}`;
  };

  const handleBarClick = (bucket: DistributionBucket) => {
    if (isRangeSelected(bucket.range)) {
      onRangeSelect(null);
    } else {
      onRangeSelect(bucket.range);
    }
  };

  const svgContainerRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const svgContainer = svgContainerRef.current;
    if (!svgContainer) {
      return;
    }

    const window = svgContainer.ownerDocument.defaultView;
    if (!window) {
      return;
    }

    const updateSize = () => {
      setWidth(svgContainer.clientWidth);
      setHeight(svgContainer.clientHeight);
    };

    updateSize();

    const observer = new window.ResizeObserver(() => {
      updateSize();
    });
    observer.observe(svgContainer);

    return () => {
      observer.disconnect();
    };
  }, []);

  const chartWidth = width - leftPadding - horizontalPadding;
  const chartHeight = height - topPadding - bottomPadding;
  const barWidth = chartWidth / distribution.length;
  const barGap = barWidth * 0.2;
  const actualBarWidth = barWidth - barGap;

  // Y-axis ticks
  const tickCount = 4;
  const yTicks: number[] = [];
  for (let i = 0; i <= tickCount; i++) {
    yTicks.push(Math.round((maxCount / tickCount) * i));
  }

  return (
    <div
      ref={svgContainerRef}
      className="relative w-full"
      style={{ height: "80px" }}
    >
      <svg
        className="absolute size-full"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ overflow: "visible" }}
      >
        {/* Y-axis grid lines */}
        {yTicks.map((tick, i) => {
          const y = topPadding + chartHeight * (1 - tick / maxCount);
          return (
            <g key={`y-tick-${i}`}>
              <line
                x1={leftPadding}
                y1={y}
                x2={width - horizontalPadding}
                y2={y}
                stroke="#e5e7eb"
                strokeWidth="1"
                strokeDasharray={i === 0 ? "0" : "3 3"}
              />
              <text
                x={leftPadding - 8}
                y={y}
                textAnchor="end"
                alignmentBaseline="middle"
                className="fill-gray-500 text-[10px] font-medium"
              >
                {tick}
              </text>
            </g>
          );
        })}

        {/* Bars */}
        {distribution.map((bucket, index) => {
          const x = leftPadding + index * barWidth + barGap / 2;
          const barHeight =
            bucket.count > 0 ? (bucket.count / maxCount) * chartHeight : 0;
          const y = topPadding + chartHeight - barHeight;

          const isSelected = isRangeSelected(bucket.range);
          const tooltipContent = getTooltipContent(bucket);

          return (
            <g key={`bar-${index}`}>
              {/* Actual bar */}
              <rect
                x={x}
                y={y}
                width={actualBarWidth}
                height={Math.max(barHeight, 0)}
                className={twMerge(
                  bucket.colors.lightFill,
                  bucket.colors.stroke,
                  "cursor-pointer outline-hidden transition-opacity duration-150",
                  selectedRange && !isSelected ? "opacity-20" : "opacity-100",
                )}
                strokeWidth="1"
                rx="2"
                ry="2"
                onClick={() => handleBarClick(bucket)}
                onMouseEnter={(event) => {
                  hoveredBarRef.current = event.currentTarget;
                  setHoveredTooltipContent(tooltipContent);
                }}
                onMouseLeave={() => setHoveredTooltipContent(null)}
                style={{ outline: "none" }}
              />

              {/* X-axis labels */}
              {(gradingValueType === "1_5_score" ||
                (gradingValueType === "yes_no" && index % 2 === 0)) && (
                <text
                  x={x + actualBarWidth / 2}
                  y={height - bottomPadding + 18}
                  textAnchor="middle"
                  className="fill-gray-500 text-[10px] font-medium"
                >
                  {bucket.range.label}
                </text>
              )}
            </g>
          );
        })}

        {/* Y-axis label */}
        <text
          x={10}
          y={topPadding + chartHeight / 2}
          textAnchor="middle"
          transform={`rotate(-90, 10, ${topPadding + chartHeight / 2})`}
          className="fill-gray-500 text-[10px] font-medium"
        >
          Teammates
        </text>
      </svg>

      {hoveredTooltipContent !== null ? (
        <AnchoredTooltip triggerRef={hoveredBarRef}>
          {hoveredTooltipContent}
        </AnchoredTooltip>
      ) : null}
    </div>
  );
};
